import * as React from "react";
import { useNavigate } from "react-router";
import {
  ArrowLeft20Regular,
  ArrowRight20Regular,
  Save20Filled,
  Search20Regular,
  Person20Regular,
  VehicleCarRegular,
  Building20Regular,
  Checkmark20Filled,
  Box20Regular,
  Dismiss20Regular,
} from "@fluentui/react-icons";
import { OpenWindowConfig, useWindowManager } from "./WindowManagerContext";
import { apiUrl } from "../../utils/api";
import { themeVars, lilaTheme, FONT_BODY, FONT_ACCENT } from "../schadenmeldung/theme";
import { initialBigAnswers, type BigAnswers, type BigDamageType } from "../schadenmeldung-big/types";
import { PhotoProvider } from "../schadenmeldung-big/PhotoUpload";
import { StepType, WhenStep } from "../schadenmeldung-big/steps/shared";
import { GebWhere, GebArt, GebHergang } from "../schadenmeldung-big/steps/gebaeude";
import { KfzWhere, KfzArt, KfzHergang } from "../schadenmeldung-big/steps/kfz";
import { SonWhere, SonDetails } from "../schadenmeldung-big/steps/sonstiges";
import { schadensartenFor, SCHADENSART_SONSTIGE } from "../schadenmeldung-big/schadensarten";

/* ─────────────── Schritt-Reihenfolge & Validierung ─────────────── */

type StepKey =
  | "kontakt" | "type" | "risikoobjekt" | "when"
  | "geb_where" | "geb_art" | "geb_hergang"
  | "kfz_where" | "kfz_art" | "kfz_hergang"
  | "son_where" | "son_details"
  | "schadensart" | "summary";

const STEP_LABEL: Record<StepKey, string> = {
  kontakt: "Kontakt", type: "Risiko", risikoobjekt: "Risikoobjekt", when: "Zeitpunkt",
  geb_where: "Ort", geb_art: "Schadenart", geb_hergang: "Hergang",
  kfz_where: "Ort", kfz_art: "Unfalltyp", kfz_hergang: "Hergang",
  son_where: "Ort", son_details: "Details",
  schadensart: "Schadensart", summary: "Zusammenfassung",
};

function buildOrder(a: BigAnswers): StepKey[] {
  const o: StepKey[] = ["kontakt", "type"];
  if (!a.damageType) return o;
  o.push("risikoobjekt", "when");
  if (a.damageType === "gebaeude") o.push("geb_where", "geb_art", "geb_hergang");
  else if (a.damageType === "kfz") o.push("kfz_where", "kfz_art", "kfz_hergang");
  else o.push("son_where", "son_details");
  o.push("schadensart", "summary");
  return o;
}

function buildDescription(a: BigAnswers): string {
  const parts: string[] = [];
  const join = (...xs: string[]) => xs.map((x) => x.trim()).filter(Boolean).join(", ");
  if (a.damageType === "gebaeude") {
    const ort = join(a.gebStrasse, a.gebPlz, a.gebOrt);
    if (ort) parts.push(`Ort: ${ort}`);
    if (a.gebSchadenart) parts.push(`Schadenart: ${a.gebSchadenart}`);
    if (a.gebSchadenstelle) parts.push(`Schadenstelle: ${a.gebSchadenstelle}`);
    if (a.gebHergang) parts.push(a.gebHergang);
  } else if (a.damageType === "kfz") {
    if (a.kfzOrt) parts.push(`Ort: ${a.kfzOrt}`);
    if (a.kfzArt) parts.push(`Unfalltyp: ${a.kfzArt}`);
  } else if (a.damageType === "sonstiges") {
    const ort = join(a.sonStrasse, a.sonPlz, a.sonOrt);
    if (ort) parts.push(`Ort: ${ort}`);
    if (a.sonSonstHergang) parts.push(a.sonSonstHergang);
    if (a.sonSacheBeschreibung) parts.push(a.sonSacheBeschreibung);
    if (a.sonMenschHergang) parts.push(a.sonMenschHergang);
  }
  return parts.join("\n");
}

/* ─────────────── Typen ─────────────── */

type ContactHit = { id: number; label: string };
type RiskObjectHit = { id: number; name: string; category: string };

/* ─────────────── Kontakt-Schritt ─────────────── */

const KontaktStep: React.FC<{
  selected: ContactHit | null;
  onSelect: (c: ContactHit | null) => void;
}> = ({ selected, onSelect }) => {
  const [q, setQ] = React.useState("");
  const [hits, setHits] = React.useState<ContactHit[]>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setHits([]); return; }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await fetch(apiUrl("contact", { search: term, limit: 20 }));
        const data = res.ok ? await res.json() : [];
        const arr = Array.isArray(data) ? data : (data.items ?? data.content ?? []);
        if (cancelled) return;
        setHits(
          arr.map((c: any) => ({
            id: Number(c.id),
            label: String(c.displayName ?? [c.firstname, c.lastname].filter(Boolean).join(" ") ?? c.company ?? `Kontakt #${c.id}`),
          })).filter((c: ContactHit) => c.id > 0),
        );
      } catch { if (!cancelled) setHits([]); }
      finally { if (!cancelled) setLoading(false); }
    }, 280);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  return (
    <div style={sx.stepBody}>
      <h2 style={sx.stepTitle}>Für welchen Kontakt wird der Schaden erfasst?</h2>
      {selected ? (
        <div style={sx.selectedRow}>
          <span style={sx.selectedIcon}><Person20Regular /></span>
          <span style={{ fontWeight: 700, color: lilaTheme.ink }}>{selected.label}</span>
          <button type="button" style={sx.clearBtn} onClick={() => onSelect(null)} aria-label="Auswahl entfernen"><Dismiss20Regular /></button>
        </div>
      ) : (
        <>
          <div style={sx.searchWrap}>
            <Search20Regular style={{ color: "#a294a0", flexShrink: 0 }} />
            <input style={sx.searchInput} placeholder="Kontakt suchen (Name, Firma)…" value={q} onChange={(e) => setQ(e.target.value)} autoFocus />
          </div>
          {loading && <div style={sx.hint}>Suche…</div>}
          {!loading && q.trim().length >= 2 && hits.length === 0 && <div style={sx.hint}>Keine Treffer.</div>}
          <div style={sx.hitList}>
            {hits.map((h) => (
              <button key={h.id} type="button" style={sx.hitRow} onClick={() => onSelect(h)}>
                <Person20Regular style={{ color: lilaTheme.primary }} /> {h.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

/* ─────────────── Risikoobjekt-Schritt ─────────────── */

const TYPE_MATCH: Record<BigDamageType, RegExp> = {
  gebaeude: /immo|gebäude|geb|haus|wohn/i,
  kfz: /kfz|fahrzeug|auto|pkw|lkw/i,
  sonstiges: /.*/,
};

const RisikoobjektStep: React.FC<{
  contactId: number | null;
  damageType: BigDamageType | null;
  selectedId: number | null;
  onSelect: (r: RiskObjectHit | null) => void;
}> = ({ contactId, damageType, selectedId, onSelect }) => {
  const [objs, setObjs] = React.useState<RiskObjectHit[]>([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!contactId) { setObjs([]); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(apiUrl(`risk-objects/by-contact/${contactId}`));
        const data = res.ok ? await res.json() : [];
        const arr = Array.isArray(data) ? data : (data.items ?? []);
        if (cancelled) return;
        setObjs(arr.map((r: any) => ({
          id: Number(r.id),
          name: String(r.name ?? r.bezeichnung ?? `Objekt #${r.id}`),
          category: String(r.category?.category ?? r.category ?? r.riskType ?? ""),
        })).filter((r: RiskObjectHit) => r.id > 0));
      } catch { if (!cancelled) setObjs([]); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [contactId]);

  const match = damageType ? TYPE_MATCH[damageType] : /.*/;
  const filtered = objs.filter((o) => match.test(o.category) || match.test(o.name));
  const shown = filtered.length > 0 ? filtered : objs;

  return (
    <div style={sx.stepBody}>
      <h2 style={sx.stepTitle}>Welches Risikoobjekt ist betroffen?</h2>
      <p style={sx.hint}>Optional — Auswahl aus den im System hinterlegten Risikoobjekten des Kontakts.</p>
      {loading && <div style={sx.hint}>Lade Risikoobjekte…</div>}
      {!loading && objs.length === 0 && <div style={sx.hint}>Für diesen Kontakt sind keine Risikoobjekte hinterlegt.</div>}
      <div style={sx.hitList}>
        {shown.map((o) => {
          const active = selectedId === o.id;
          return (
            <button key={o.id} type="button" style={{ ...sx.optionCard, ...(active ? sx.optionCardActive : {}) }} onClick={() => onSelect(active ? null : o)}>
              <span style={sx.selectedIcon}>{damageType === "kfz" ? <VehicleCarRegular /> : damageType === "gebaeude" ? <Building20Regular /> : <Box20Regular />}</span>
              <span style={{ flex: 1, textAlign: "left" }}>
                <span style={{ fontWeight: 700, color: lilaTheme.ink, display: "block" }}>{o.name}</span>
                {o.category && <span style={{ fontSize: "0.8rem", color: "#6b5a66" }}>{o.category}</span>}
              </span>
              {active && <Checkmark20Filled style={{ color: lilaTheme.primary }} />}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* ─────────────── Schadensart-Schritt ─────────────── */

const SchadensartStep: React.FC<{
  damageType: BigDamageType | null;
  value: string;
  onChange: (v: string) => void;
}> = ({ damageType, value, onChange }) => {
  const options = schadensartenFor(damageType);
  const isCustom = value !== "" && !options.includes(value);
  const sonstigeActive = value === SCHADENSART_SONSTIGE || isCustom;
  // value ist die einzige Quelle der Wahrheit — Freitext wird daraus abgeleitet
  // (kein separater State → keine Desynchronisierung).
  const freeText = isCustom ? value : "";

  return (
    <div style={sx.stepBody}>
      <h2 style={sx.stepTitle}>Welche Schadensart liegt vor?</h2>
      <div style={sx.chipGrid}>
        {options.map((opt) => {
          const active = opt === SCHADENSART_SONSTIGE ? sonstigeActive : value === opt;
          return (
            <button key={opt} type="button" style={{ ...sx.chip, ...(active ? sx.chipActive : {}) }}
              onClick={() => onChange(opt)}>
              {opt}
            </button>
          );
        })}
      </div>
      {sonstigeActive && (
        <div style={{ marginTop: 14 }}>
          <label style={sx.fieldLabel}>Schadensart (Freitext)</label>
          <input style={sx.searchInput as React.CSSProperties} value={freeText} placeholder="Schadensart eingeben"
            onChange={(e) => onChange(e.target.value || SCHADENSART_SONSTIGE)} />
        </div>
      )}
    </div>
  );
};

/* ─────────────── Window-Shell ─────────────── */

const SchadenFormWindow: React.FC<{ windowId: string; contactId?: number | null; contactName?: string | null }> = ({ windowId, contactId: initialContactId, contactName: initialContactName }) => {
  const { closeWindow } = useWindowManager();
  const navigate = useNavigate();

  const [contact, setContact] = React.useState<ContactHit | null>(
    initialContactId ? { id: initialContactId, label: initialContactName?.trim() || `Kontakt #${initialContactId}` } : null,
  );
  const [riskObject, setRiskObject] = React.useState<RiskObjectHit | null>(null);
  const [schadensart, setSchadensart] = React.useState("");
  const [answers, setAnswers] = React.useState<BigAnswers>(initialBigAnswers);
  const [stepKey, setStepKey] = React.useState<StepKey>("kontakt");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [photos, setPhotos] = React.useState<File[]>([]);

  const photoCtx = React.useMemo(() => ({
    files: photos,
    addFiles: (fs: File[]) => setPhotos((p) => [...p, ...fs]),
    removeFile: (i: number) => setPhotos((p) => p.filter((_, j) => j !== i)),
  }), [photos]);

  const update = React.useCallback((patch: Partial<BigAnswers>) => setAnswers((p) => ({ ...p, ...patch })), []);
  const stepProps = { answers, update };

  const order = React.useMemo(() => buildOrder(answers), [answers]);
  const currentIndex = Math.max(0, order.indexOf(stepKey));

  const canAdvance = (s: StepKey): boolean => {
    switch (s) {
      case "kontakt": return contact != null;
      case "type": return answers.damageType != null;
      case "when": return answers.schadenDatum.trim().length > 0 || answers.entdecktAm.trim().length > 0;
      case "geb_where": return answers.gebOrt.trim().length > 0 || answers.gebStrasse.trim().length > 0;
      case "kfz_where": return answers.kfzOrt.trim().length > 0;
      case "son_where": return answers.sonOrt.trim().length > 0;
      case "geb_art": return answers.gebSchadenart != null;
      case "kfz_art": return answers.kfzArt != null;
      case "schadensart": return schadensart.trim().length > 0;
      default: return true;
    }
  };

  // Sicherheitsnetz: fällt der aktuelle Schritt durch einen Risiko-Typ-Wechsel aus der
  // Reihenfolge, auf einen gültigen Schritt zurücksetzen (Navigation bleibt heil).
  React.useEffect(() => {
    if (order.indexOf(stepKey) === -1) setStepKey("type");
  }, [order, stepKey]);

  const goBack = () => { const i = order.indexOf(stepKey); if (i > 0) setStepKey(order[i - 1]); };
  const goNext = async () => {
    if (!canAdvance(stepKey)) return;
    if (stepKey === "summary") { await submit(); return; }
    const i = order.indexOf(stepKey);
    if (i >= 0 && i < order.length - 1) setStepKey(order[i + 1]);
  };

  const submit = async () => {
    if (!contact) { setError("Bitte zuerst einen Kontakt auswählen."); setStepKey("kontakt"); return; }
    setSaving(true);
    setError(null);
    try {
      const desc = buildDescription(answers);
      const body: Record<string, unknown> = {
        contact: { id: contact.id },
        status: "O",
        damageType: schadensart || undefined,
        damageDate: answers.schadenDatum ? answers.schadenDatum.slice(0, 10) : undefined,
        reportDate: new Date().toISOString().slice(0, 10),
        title: [schadensart, contact.label].filter(Boolean).join(" — ") || undefined,
        description: desc || undefined,
      };
      if (riskObject) body.riskObject = { id: riskObject.id };
      const res = await fetch(apiUrl("damage"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`Anlegen fehlgeschlagen (HTTP ${res.status})`);
      const created = await res.json();
      closeWindow(windowId);
      if (created?.id) navigate(`/schaeden/${created.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Der Schaden konnte nicht angelegt werden.");
    } finally {
      setSaving(false);
    }
  };

  const renderStep = () => {
    switch (stepKey) {
      case "kontakt": return <KontaktStep selected={contact} onSelect={setContact} />;
      case "type": return <StepType {...stepProps} />;
      case "risikoobjekt": return <RisikoobjektStep contactId={contact?.id ?? null} damageType={answers.damageType} selectedId={riskObject?.id ?? null} onSelect={setRiskObject} />;
      case "when": return <WhenStep {...stepProps} />;
      case "geb_where": return <GebWhere {...stepProps} />;
      case "geb_art": return <GebArt {...stepProps} />;
      case "geb_hergang": return <GebHergang {...stepProps} />;
      case "kfz_where": return <KfzWhere {...stepProps} />;
      case "kfz_art": return <KfzArt {...stepProps} />;
      case "kfz_hergang": return <KfzHergang {...stepProps} />;
      case "son_where": return <SonWhere {...stepProps} />;
      case "son_details": return <SonDetails {...stepProps} />;
      case "schadensart": return <SchadensartStep damageType={answers.damageType} value={schadensart} onChange={setSchadensart} />;
      case "summary": return (
        <div style={sx.stepBody}>
          <h2 style={sx.stepTitle}>Zusammenfassung</h2>
          <div style={sx.sumGrid}>
            <div style={sx.sumLabel}>Kontakt</div><div style={sx.sumValue}>{contact?.label ?? "—"}</div>
            <div style={sx.sumLabel}>Risiko-Typ</div><div style={sx.sumValue}>{answers.damageType ? STEP_LABEL.type + ": " + (answers.damageType === "gebaeude" ? "Immobilie" : answers.damageType === "kfz" ? "KFZ" : "Sonstiges") : "—"}</div>
            <div style={sx.sumLabel}>Risikoobjekt</div><div style={sx.sumValue}>{riskObject?.name ?? "—"}</div>
            <div style={sx.sumLabel}>Schadentag</div><div style={sx.sumValue}>{answers.schadenDatum ? answers.schadenDatum.slice(0, 10) : "—"}</div>
            <div style={sx.sumLabel}>Schadensart</div><div style={sx.sumValue}>{schadensart || "—"}</div>
            <div style={sx.sumLabel}>Beschreibung</div><div style={sx.sumValue}>{buildDescription(answers) || "—"}</div>
          </div>
          {error && <div style={sx.errorBox}>{error}</div>}
        </div>
      );
      default: return null;
    }
  };

  return (
    <PhotoProvider value={photoCtx}>
      <div style={{ ...themeVars(lilaTheme), ...sx.root }}>
        {/* Kopf */}
        <div style={sx.header}>
          <div style={sx.brandLine}>SCHADEN ANLEGEN</div>
          <h1 style={sx.brandTitle}>Neuer Schaden{contact ? ` · ${contact.label}` : ""}</h1>
          <div style={sx.dots}>
            {order.filter((k) => k !== "summary").map((k, i) => (
              <span key={k} title={STEP_LABEL[k]} style={{ ...sx.dot, ...(i <= currentIndex ? sx.dotActive : {}) }} />
            ))}
          </div>
        </div>

        {/* Inhalt */}
        <div style={sx.body}>
          <div style={sx.card}>{renderStep()}</div>
        </div>

        {/* Fußleiste */}
        <div style={sx.footer}>
          <button type="button" style={sx.ghostBtn} onClick={() => closeWindow(windowId)}>Abbrechen</button>
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button type="button" style={{ ...sx.navBtn, ...sx.backBtn }} onClick={goBack} disabled={currentIndex === 0 || saving}>
              <ArrowLeft20Regular /> Zurück
            </button>
            <button type="button" style={{ ...sx.navBtn, ...sx.fwdBtn, ...(!canAdvance(stepKey) || saving ? sx.fwdBtnDisabled : {}) }} onClick={goNext} disabled={!canAdvance(stepKey) || saving}>
              {stepKey === "summary" ? <><Save20Filled /> {saving ? "Speichere…" : "Schaden anlegen"}</> : <>Weiter <ArrowRight20Regular /></>}
            </button>
          </div>
        </div>
      </div>
    </PhotoProvider>
  );
};

/* ─────────────── Styles ─────────────── */

const sx: Record<string, React.CSSProperties> = {
  root: { display: "flex", flexDirection: "column", height: "100%", backgroundColor: "#f4f5f8", color: lilaTheme.ink, fontFamily: FONT_BODY },
  header: { textAlign: "center", padding: "14px 24px 10px", borderBottom: `1px solid ${lilaTheme.cardBorder}`, backgroundColor: "#ffffff", flexShrink: 0 },
  brandLine: { fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.16em", color: lilaTheme.accent },
  brandTitle: { margin: "2px 0 0", fontFamily: FONT_ACCENT, fontWeight: 500, fontSize: "1.3rem", color: lilaTheme.ink },
  dots: { display: "flex", justifyContent: "center", gap: 6, marginTop: 10 },
  dot: { width: 8, height: 8, borderRadius: 999, backgroundColor: "#d9d2dc" },
  dotActive: { backgroundColor: lilaTheme.primary },
  body: { flex: 1, overflowY: "auto", padding: "20px 24px" },
  card: { backgroundColor: "#ffffff", borderRadius: 16, border: `1px solid ${lilaTheme.cardBorder}`, padding: "24px", boxShadow: "0 8px 28px rgba(45,20,43,0.08)" },
  stepBody: { display: "flex", flexDirection: "column", gap: 12 },
  stepTitle: { margin: 0, fontFamily: FONT_ACCENT, fontWeight: 500, fontSize: "1.25rem", color: lilaTheme.ink },
  hint: { fontSize: "0.85rem", color: "#6b5a66" },
  fieldLabel: { fontSize: "0.78rem", fontWeight: 700, color: "#6b5a66", textTransform: "uppercase", letterSpacing: "0.04em", display: "block", marginBottom: 4 },
  searchWrap: { display: "flex", alignItems: "center", gap: 8, backgroundColor: "#f8f4f8", borderRadius: 10, padding: "10px 14px", border: `1px solid ${lilaTheme.cardBorder}` },
  searchInput: { border: 0, background: "transparent", outline: "none", fontFamily: FONT_BODY, fontSize: "0.95rem", color: lilaTheme.ink, flex: 1, width: "100%", padding: "8px 0" },
  hitList: { display: "flex", flexDirection: "column", gap: 8, maxHeight: 320, overflowY: "auto", marginTop: 4 },
  hitRow: { display: "flex", alignItems: "center", gap: 10, padding: "11px 14px", borderRadius: 10, border: `1px solid ${lilaTheme.cardBorder}`, backgroundColor: "#ffffff", cursor: "pointer", fontFamily: FONT_BODY, fontSize: "0.92rem", color: lilaTheme.ink, textAlign: "left" },
  optionCard: { display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", borderRadius: 12, border: `1.5px solid ${lilaTheme.cardBorder}`, backgroundColor: "#ffffff", cursor: "pointer", fontFamily: FONT_BODY },
  optionCardActive: { borderColor: lilaTheme.primary, backgroundColor: lilaTheme.accentSoft },
  selectedRow: { display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 12, border: `1.5px solid ${lilaTheme.primary}`, backgroundColor: lilaTheme.accentSoft },
  selectedIcon: { width: 34, height: 34, flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", backgroundColor: lilaTheme.accentSoft, color: lilaTheme.primary, borderRadius: 9 },
  clearBtn: { marginLeft: "auto", border: 0, background: "transparent", cursor: "pointer", color: "#6b5a66" },
  chipGrid: { display: "flex", flexWrap: "wrap", gap: 8 },
  chip: { padding: "9px 14px", borderRadius: 999, border: `1.5px solid ${lilaTheme.cardBorder}`, backgroundColor: "#ffffff", color: lilaTheme.ink, fontFamily: FONT_BODY, fontSize: "0.88rem", fontWeight: 600, cursor: "pointer" },
  chipActive: { borderColor: lilaTheme.primary, backgroundColor: lilaTheme.primary, color: "#ffffff" },
  sumGrid: { display: "grid", gridTemplateColumns: "140px 1fr", gap: "8px 16px", marginTop: 4 },
  sumLabel: { fontSize: "0.78rem", fontWeight: 700, color: "#6b5a66", textTransform: "uppercase", letterSpacing: "0.04em" },
  sumValue: { fontSize: "0.92rem", color: lilaTheme.ink, whiteSpace: "pre-wrap" },
  errorBox: { marginTop: 12, padding: "10px 12px", borderRadius: 8, backgroundColor: "#fbecec", color: "#c23b3b", fontSize: "0.88rem" },
  footer: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "12px 20px", borderTop: `1px solid ${lilaTheme.cardBorder}`, backgroundColor: "#ffffff", flexShrink: 0 },
  ghostBtn: { border: `1px solid ${lilaTheme.cardBorder}`, background: "#ffffff", color: "#6b5a66", borderRadius: 10, padding: "9px 16px", fontWeight: 600, fontSize: "0.88rem", cursor: "pointer", fontFamily: FONT_BODY },
  navBtn: { display: "inline-flex", alignItems: "center", gap: 6, borderRadius: 10, padding: "9px 18px", fontWeight: 600, fontSize: "0.9rem", cursor: "pointer", fontFamily: FONT_BODY, border: 0 },
  backBtn: { backgroundColor: "#f0eaf2", color: lilaTheme.primary },
  fwdBtn: { backgroundColor: lilaTheme.primary, color: "#ffffff", boxShadow: "0 6px 16px -6px rgba(77,32,81,0.6)" },
  fwdBtnDisabled: { opacity: 0.5, cursor: "not-allowed", boxShadow: "none" },
};

/* ─────────────── Opener ─────────────── */

let schadenDraftCounter = 0;

export const openSchadenFormWindow = (
  openWindow: (config: OpenWindowConfig) => void,
  params: { contactId?: number | null; contactName?: string | null } = {},
): string => {
  const { contactId, contactName } = params;
  const suffix = contactName?.trim() ? contactName.trim() : contactId ? `Kontakt #${contactId}` : `Entwurf #${++schadenDraftCounter}`;
  const windowId = `schaden-create-c${contactId ?? "new"}-${Date.now()}`;
  openWindow({
    id: windowId,
    title: `Neuer Schaden · ${suffix}`,
    icon: <Box20Regular />,
    initialSize: { width: 960, height: 760 },
    render: () => <SchadenFormWindow windowId={windowId} contactId={contactId ?? null} contactName={contactName ?? null} />,
  });
  return windowId;
};

export default SchadenFormWindow;
